import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

export default function ScanMonitorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const inviteToken = searchParams.get('t') || '';

  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);
  const [invalidLink, setInvalidLink] = useState(!inviteToken);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/register-monitor', {
        invite_token: inviteToken,
        name:         name.trim(),
        email:        email.trim(),
        password,
      });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al registrar';
      if (err.response?.status === 403) {
        setInvalidLink(true);
      } else {
        toast.error(detail);
      }
    }
    setSubmitting(false);
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center overflow-y-auto"
      style={{
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundColor: 'rgba(72,41,58,0.42)', zIndex: 0 }} />

      <div className="relative w-full max-w-md px-4 py-10" style={{ zIndex: 10 }}>
        <div className="flex justify-center mb-6">
          <img src="/logo.png" alt="El Club de Manri" style={{ width: 'min(70vw, 300px)', height: 'auto' }} className="drop-shadow-2xl" />
        </div>

        <div className="rounded-3xl shadow-2xl p-8" style={{ backgroundColor: 'rgba(251,248,243,0.97)' }}>
          {invalidLink ? (
            <div className="text-center space-y-3">
              <p className="font-heading font-bold text-xl text-[#48293A]">Enlace no válido</p>
              <p className="text-[#4C4D57] text-sm">Este enlace de registro de monitores no es válido o ha sido regenerado. Solicita un nuevo enlace al administrador.</p>
              <button onClick={() => navigate('/')} className="btn-manrique text-sm mt-2">Ir al inicio</button>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="monitor-register-success">
              <div className="w-16 h-16 rounded-full bg-[#27AE60]/10 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-[#27AE60]" />
              </div>
              <p className="font-heading font-bold text-xl text-[#48293A]">Registro recibido</p>
              <p className="text-[#4C4D57] text-sm leading-relaxed">
                Un administrador activará tu cuenta antes de que puedas entrar. Recibirás acceso cuando sea aprobado.
              </p>
              <button onClick={() => navigate('/')} className="text-sm text-[#48293A] hover:underline font-medium">
                Volver al inicio
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-bold text-2xl tracking-tight text-[#48293A] mb-1">
                Alta de monitor
              </h1>
              <p className="text-[#4C4D57] text-sm mb-6">
                Rellena tus datos. Un administrador activará tu cuenta.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Nombre completo</label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="input-manrique"
                    required
                    placeholder="Tu nombre"
                    data-testid="monitor-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="input-manrique"
                    required
                    placeholder="tu@email.com"
                    data-testid="monitor-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="input-manrique pr-10"
                      required
                      minLength={8}
                      data-testid="monitor-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#48293A]"
                    >
                      {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                  {password.length > 0 && password.length < 8 && (
                    <p className="text-xs text-red-500 mt-1">Mínimo 8 caracteres</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">Confirmar contraseña</label>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    className="input-manrique"
                    required
                    data-testid="monitor-confirm-input"
                  />
                  {confirm.length > 0 && confirm !== password && (
                    <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-manrique w-full flex items-center justify-center gap-2 mt-2"
                  data-testid="monitor-register-submit"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Registrarme como monitor <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
